import {CommercePlatformCartServiceModelsPromotionEvaluationStatus} from "../../api/openApi/cartService";
import {CartPromotionEvaluationType} from "../../redux/slices/carts";

export class PromotionEvaluationModel {
    private readonly _promotionCode: string | undefined;
    private readonly _status: "Redeemed" | "Unknown" | "UnderMinimumOrderValue" | "Valid" | "DoesntApplyItems" | "NotStarted" | "Expired" | "NotEligible" | "Invalid" | undefined;
    private readonly _number: string | null | undefined;
    private readonly _type: "ProductGroup" | "SingleUseCode" | "SingleProduct" | "Unknown" | undefined;
    private readonly _description: string | null | undefined;
    private readonly _start: string | undefined;
    private readonly _end: string | undefined;
    private readonly _minAmount: number | null | undefined;
    private readonly _minQuantity: number | null | undefined;

    constructor(promotionCode: string | undefined, promotionEvaluation: CartPromotionEvaluationType) {
        this._promotionCode = promotionCode;
        this._status = promotionEvaluation.status;
        this._number = promotionEvaluation.number;
        this._type = promotionEvaluation.type;
        this._description = promotionEvaluation.description;
        this._start = promotionEvaluation.start;
        this._end = promotionEvaluation.end;
        this._minAmount = promotionEvaluation.minAmount;
        this._minQuantity = promotionEvaluation.minQuantity;
    }

    /**
     * @method status
     * @description Returns the status of the promotion evaluation.
     * @returns {CommercePlatformCartServiceModelsPromotionEvaluationStatus | undefined}
     */
    public get status(): "Redeemed" | "Unknown" | "UnderMinimumOrderValue" | "Valid" | "DoesntApplyItems" | "NotStarted" | "Expired" | "NotEligible" | "Invalid" | undefined {
        return this._status;
    }

    /**
     * @method number
     * @description Returns the number of the promotion evaluation.
     * @returns {string | null | undefined}
     */
    public get number(): string | null | undefined {
        return this._number ?? this._promotionCode;
    }

    /**
     * @method type
     * @description Returns the type of the promotion evaluation.
     * @returns {CommercePlatformCartServiceModelsPromotionType | undefined}
     */
    public get type(): "ProductGroup" | "SingleUseCode" | "SingleProduct" | "Unknown" | undefined {
        return this._type;
    }

    /**
     * @method description
     * @description Returns the description of the promotion evaluation.
     * @returns {string | null | undefined}
     */
    public get description(): string | null | undefined {
        return this._description;
    }

    /**
     * @method start
     * @description Returns the start date of the promotion evaluation.
     * @returns {string | undefined}
     */
    public get start(): string | undefined {
        return this._start;
    }

    /**
     * @method end
     * @description Returns the end date of the promotion evaluation.
     * @returns {string | undefined}
     */
    public get end(): string | undefined {
        return this._end;
    }

    /**
     * @method minAmount
     * @description Returns the minimum amount of the promotion evaluation.
     * @returns {number | null | undefined}
     */
    public get minAmount(): number | null | undefined {
        return this._minAmount;
    }

    /**
     * @method minQuantity
     * @description Returns the minimum quantity of the promotion evaluation.
     * @returns {number | null | undefined}
     */
    public get minQuantity(): number | null | undefined {
        return this._minQuantity;
    }

    /**
     * @method message
     * @description Returns the display message for promotion evaluation.
     * @returns {string}
     */
    public get message(): string {
        switch (this.status) {
            case CommercePlatformCartServiceModelsPromotionEvaluationStatus.Valid:
                return '';
            case CommercePlatformCartServiceModelsPromotionEvaluationStatus.Invalid:
                return `The coupon code '${(this.number)}' is not valid. Please ensure the code is correctly entered and try again.`;
            case CommercePlatformCartServiceModelsPromotionEvaluationStatus.NotStarted:
                return `This coupon code does not take effect until ${this.convertESTDateInAWSFormat(this.start)}.`;
            case CommercePlatformCartServiceModelsPromotionEvaluationStatus.Expired:
                return `This coupon code expired on ${this.convertESTDateInAWSFormat(this.end)}.`;
            case CommercePlatformCartServiceModelsPromotionEvaluationStatus.DoesntApplyItems:
                return "This coupon is restricted to only certain products or charges. Please see the original promotion for more details.";
            case CommercePlatformCartServiceModelsPromotionEvaluationStatus.UnderMinimumOrderValue:
                return `This order does not meet the minimum of $${this.minAmount} for this coupon.`;
            case CommercePlatformCartServiceModelsPromotionEvaluationStatus.NotEligible:
                return "Items in your cart are not eligible for this promotion. Please contact us for assistance.";
            case CommercePlatformCartServiceModelsPromotionEvaluationStatus.Redeemed:
                return "This coupon code has already been redeemed.";
            case CommercePlatformCartServiceModelsPromotionEvaluationStatus.Unknown:
                return `The coupon code '${(this.number)}' is not valid. Please ensure the code is correctly entered and try again.`;
            default: throw new Error(`Unknown promotion evaluation status: ${this.status}`);
        }
    }

    /**
     * Promotions from the ERP AppSync API are returned in EST format.
     * Since they are only used for display purposes, perform raw string manipulation to convert them to C#'s ToShortDateString() format (MM/DD/YYYY for US locale)
     * Important: Any change to the date format or its timezone will break this function.
     * We should upgrade the date to the UTC format in the future.
     * @param {string} estDateInAWSFormat
     * @returns {string}
     * @private
     */
    private convertESTDateInAWSFormat(estDateInAWSFormat: string | undefined): string {
        if (!estDateInAWSFormat) return '';
        // AWS Format: '2023-04-04T00:00:00Z';
        // Extract the date portion from the raw string
        const extractedDate = estDateInAWSFormat.split('T')[0]; // This will give '2023-04-04'
        // Format the date to match C#'s ToShortDateString() format (MM/DD/YYYY for US locale)
        const formattedDateParts = extractedDate.split('-');
        // Outputs: 04/04/2023
        return `${formattedDateParts[1]}/${formattedDateParts[2]}/${formattedDateParts[0]}`
    }

}